// Application bootstrap conductor. Each phase lives in @app/bootstrap/* and
// owns one slice of startup (three stack, data, scene graph, input, UI, loop).
// This file is just the order in which they fire.

import { store } from '@store/instance';
import { INTENTS } from '@store/intents';
import { registerAppIntents } from './intents';
import { installReduxDevtools } from './devtools';
import { loadSceneJson } from './sceneIO';
import { seedZeroField, tryLoadField } from './datasetManager';
import { createThreeStack } from './bootstrap/threeStack';
import { wireFieldDependentSteps } from './bootstrap/data';
import { wireSceneGraph } from './bootstrap/scene';
import { wireInput } from './bootstrap/input';
import { wireUI } from './bootstrap/ui';
import { startAnimateLoop } from './bootstrap/loop';
import { settleInitialPaneState } from '@ui/dom';
import type { ViewerContext } from './context';

/** Bootstrap the viewer. Resolves once UI + scene wiring is complete. */
export async function bootstrap(viewer: ViewerContext): Promise<void> {
    registerAppIntents(store, viewer);
    installReduxDevtools(store);
    createThreeStack(viewer);

    // Phase messages stay live for the whole boot. Depth (see ui/loading.ts)
    // goes outer SHOW=1 → inner SHOW=2 → inner HIDE=1 across each await, so
    // the overlay never blinks closed mid-bootstrap. Each new SHOW gets its
    // own try/finally so a throw cannot leak depth and strand the overlay.
    store.dispatch(INTENTS.UI_LOADING_SHOW, { msg: 'Loading scene…' });
    try {
        await loadSceneJson(viewer, 'data/scene_default.json');

        // tryLoadField → fieldData.ts dispatches its own SHOW('Loading dataset…')
        // / HIDE pair, so the message swaps to the dataset phase here.
        const loaded = await tryLoadField(viewer);
        if (!loaded) {
            console.warn('HDF5 not loaded; using zero-field fallback');
            seedZeroField(viewer);
        }

        store.dispatch(INTENTS.UI_LOADING_SHOW, { msg: 'Building scene…' });
        try {
            await wireFieldDependentSteps(viewer);
        } finally {
            store.dispatch(INTENTS.UI_LOADING_HIDE);
        }
    } catch (e) {
        console.error('Field load failed, using zero-field fallback:', e);
        seedZeroField(viewer);
    }

    wireSceneGraph(viewer);
    wireInput(viewer);
    wireUI(store, viewer);

    // Render one frame synchronously so the canvas has pixels before we drop
    // the overlay; otherwise there is a one-tick window where the overlay is
    // hidden but the canvas is still empty.
    const { renderer, scene, camera } = viewer.three;
    if (renderer && scene && camera) renderer.render(scene, camera);
    store.dispatch(INTENTS.UI_LOADING_HIDE);

    // Settle each pane's initial collapsed state while the chrome is still
    // invisible (body.app-loading), then drop the class so the staggered
    // .pane opacity transition reveals the *final* state, not a mid-mutation.
    settleInitialPaneState(viewer);
    requestAnimationFrame(() => {
        document.body.classList.remove('app-loading');
        // Readiness signal for automated capture (scripts/shot-readme.ts): the canvas has
        // pixels and the chrome has settled into its final state. Cheaper and far more
        // reliable than a screenshot script guessing with a timer.
        performance.mark('magviz:first-frame');
    });

    startAnimateLoop(viewer);
}
